import { useEffect } from "react";

/** The `?photoSizeId=` a link names, when it is a size this shop sells; else null. */
export function validUrlPhotoSizeId(
  param: string | null | undefined,
  options: { id: string }[],
): string | null {
  if (!param) return null;
  return options.some((option) => option.id === param) ? param : null;
}

/**
 * The size to restore from the cart when the order is reloaded, or null to
 * leave the form alone. A valid link size wins (restoring the cart lands after
 * the link's size was applied and would undo it); an invalid one is ignored,
 * so the cart's size is kept exactly as before bundles. While the sizes are not
 * loaded yet nothing is valid, so the cart's size goes in now and
 * useUrlPhotoSize applies the link's size once they load.
 */
export function cartRestorePhotoSizeId(
  param: string | null | undefined,
  options: { id: string }[],
  cartPhotoSizeId: string | null | undefined,
): string | null {
  if (!cartPhotoSizeId) return null;
  return validUrlPhotoSizeId(param, options) ? null : cartPhotoSizeId;
}

/**
 * Selects the size a link names, also when the link is followed on the
 * already-mounted page (the bundle slots panel, the cart drawer): the size
 * initialisation in NewOrder runs once per page load, so without this a
 * client-side navigation to `?photoSizeId=` changed the URL and nothing else.
 */
export function useUrlPhotoSize(
  param: string | null,
  options: { id: string }[],
  apply: (photoSizeId: string) => void,
): void {
  const valid = validUrlPhotoSizeId(param, options);
  useEffect(() => {
    if (valid) apply(valid);
    // `apply` is react-hook-form's setValue; only a new valid id should re-apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid]);
}
